import { GatewayLinkSetup } from '../shared/GatewayLinkSetup'
import { PemFileInput } from '../shared/PemFileInput'
import * as React from 'react'
import { useLazyGetTagImportTemplateQuery } from '@axilon-apps/domains-ontology-ui-state'
import { AlertTriangle, ChevronDown, Download } from 'lucide-react'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Textarea } from '../../../components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group'
import { cn } from '../../../lib/utils'
import FileDropZone from '../shared/FileDropZone'
import { SOURCE_LABELS, defaultSub, specFor } from './constants'
import { DumpFilePreview } from './DumpPreview'
import { externalHistoryCredentials, parseExternalHistorySources } from './externalHistory'
import { ExternalHistoryMappingStudio } from './ExternalHistoryMappingStudio'
import { TagMappingStudio } from './TagMappingPreview'
import { MappingSkipConfirmDialog } from './MappingSkipConfirmDialog'
import { SoapContractPreview } from './SoapContractPreview'
import { TabularFileFields } from './TabularFileFields'
import {
  defersExternalHistoryMapping,
  fileMatchesAccept,
  isExternalHistorySource,
  isOptionalPrepSkipped,
  isSourcePrepReady,
  orderedSelected
} from './plan'
import { resolveSiteId } from './siteIds'
import { WizardChrome } from './WizardChrome'
import type { SourceId, SubOptionId } from './types'
import type { IngestWizardApi } from './useIngestWizard'

interface StepCollectProps {
  wizard: IngestWizardApi
}

export function StepCollect({ wizard }: StepCollectProps) {
  const {
    state,
    siteName,
    updatePrep,
    setOpenPrep,
    setSub,
    collectBack,
    collectNext,
    returnToRun,
    prepReady
  } = wizard
  const ids = orderedSelected(state.selected)
  const siteId = resolveSiteId({
    mode: state.mode,
    existingSiteId: state.existingSiteId,
    newSiteName: state.newSiteName || siteName
  })
  const skippedMappings = ids.filter(
    (id): id is 'ipmap' | 'roboticsmap' =>
      (id === 'ipmap' || id === 'roboticsmap') && isOptionalPrepSkipped(state, id)
  )
  const [confirmSkipOpen, setConfirmSkipOpen] = React.useState(false)
  const [getTagImportTemplate, tagImportTemplateState] = useLazyGetTagImportTemplateQuery()
  const [templateError, setTemplateError] = React.useState<string | null>(null)

  const downloadTagImportTemplate = (protocol: 'webdev' | 'opc') => {
    setTemplateError(null)
    void getTagImportTemplate(protocol)
      .unwrap()
      .then((csv) => {
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download =
          protocol === 'opc'
            ? 'opc-tag-import-direct-template.csv'
            : 'opc-tag-import-webdev-template.csv'
        anchor.click()
        URL.revokeObjectURL(url)
      })
      .catch(() => setTemplateError('Could not download the CSV template.'))
  }

  const continueToNext = () => {
    if (state.run.editing) returnToRun()
    else collectNext()
  }

  const requestContinue = () => {
    if (skippedMappings.length > 0) {
      setConfirmSkipOpen(true)
      return
    }
    continueToNext()
  }

  return (
    <WizardChrome
      eyebrow={state.flow === 'modify' ? 'Step 03 — Prepare' : 'Step 03 — Prepare'}
      title={`Prepare uploads for ${siteName || 'this site'}`}
      description="Fill in each source in ingest order. Mapping uploads are recommended and can be skipped after confirmation."
      onBack={collectBack}
      onContinue={requestContinue}
      continueDisabled={!prepReady}
      continueLabel={state.run.editing ? 'Return to run' : 'Continue'}
    >
      <div className="flex flex-col gap-2">
        {ids.map((id, index) => {
          const sub = state.sub[id] ?? defaultSub(id)
          const prep = state.prep[id]
          const open = state.openPrep === id
          const optionalSkipped = isOptionalPrepSkipped(state, id)
          const ready = optionalSkipped || isSourcePrepReady(state, id)
          const selectedNames = prep
            ? prep.files.length > 0
              ? prep.files.map((file) => file.name)
              : prep.file
                ? [prep.file.name]
                : (prep.stagedFiles?.map((file) => file.name) ?? [])
            : []
          const primaryName = selectedNames[0]
          const spec = specFor(
            id,
            sub,
            prep?.vendor,
            primaryName,
            prep?.tagMode,
            state.flow,
            selectedNames.length
          )
          // Tabular files are judged against the pinned config's accept list by
          // TabularFileFields, which shows its own message.
          const mismatchedFileName =
            id !== 'tabular' &&
            !!spec.drop &&
            selectedNames.find((name) => !fileMatchesAccept(name, spec.drop?.accept))
          const fileMismatch = !!mismatchedFileName
          return (
            <div
              key={id}
              className={cn(
                'overflow-hidden rounded-lg border',
                optionalSkipped ? 'border-st-warn/40' : ready ? 'border-st-run/40' : 'border-line',
                'bg-bg-elev'
              )}
            >
              <button
                type="button"
                onClick={() => setOpenPrep(open ? null : id)}
                aria-expanded={open}
                className="flex w-full items-center gap-3 border-0 bg-transparent px-3.5 py-3 text-left"
              >
                <span className="shrink-0 font-mono text-11 text-ink-3">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-14 font-medium text-ink">{SOURCE_LABELS[id]}</span>
                  <span className="mt-0.5 block text-xs text-ink-3">
                    {optionalSkipped
                      ? 'Recommended mapping upload — optional'
                      : fileMismatch
                        ? 'Selected file does not match this source system'
                        : ready
                          ? prep && prep.files.length > 1
                            ? `${prep.files.length} files selected`
                            : prep?.file
                              ? prep.file.name
                              : prep?.files[0]
                                ? prep.files[0].name
                                : prep?.stagedFiles?.[0]
                                  ? `${prep.stagedFiles[0].name} staged`
                                  : 'Connection details set'
                          : spec.drop
                            ? 'File and details required'
                            : 'Connection details required'}
                  </span>
                </span>
                <Badge
                  variant={ready ? 'secondary' : 'outline'}
                  className={cn(
                    'shrink-0',
                    optionalSkipped
                      ? 'border-transparent bg-st-warn-bg text-st-warn'
                      : ready && 'border-transparent bg-st-run-bg text-st-run'
                  )}
                >
                  {optionalSkipped ? 'Optional' : ready ? 'Ready' : 'Needs input'}
                </Badge>
                <ChevronDown
                  className={cn(
                    'size-4 shrink-0 text-ink-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
                    open && 'rotate-180'
                  )}
                />
              </button>

              <div
                data-open={open}
                className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
              >
                <div className="overflow-hidden">
                  {prep ? (
                    <div className="flex flex-col gap-3.5 px-3.5 pb-4">
                      {state.run.errors[id] ? (
                        <div
                          role="alert"
                          className="rounded-md border border-st-down/30 bg-st-down-bg px-3 py-2 text-xs text-st-down"
                        >
                          Previous attempt failed: {state.run.errors[id]}
                        </div>
                      ) : null}
                      {(prep.missingFiles?.length ?? 0) > 0 ? (
                        <div
                          role="alert"
                          className="rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
                        >
                          Re-attach {prep.missingFiles?.map((file) => file.name).join(', ')}.
                          Browsers cannot restore file contents after a page refresh.
                        </div>
                      ) : null}
                      {id === 'scada' || id === 'erp' || id === 'mes' || id === 'safetymanager' ? (
                        <SourceSubSeg id={id} value={sub} onChange={(v) => setSub(id, v)} />
                      ) : null}

                      {spec.note ? (
                        <div className="rounded-md border border-line-2 bg-bg-sunk px-3 py-2 text-xs leading-snug text-ink-2">
                          {spec.note}
                        </div>
                      ) : null}

                      {spec.seg && !spec.seg.after ? (
                        <SegControl
                          label={spec.seg.label}
                          value={
                            spec.seg.key === 'vendor' ? (prep.vendor ?? '') : (prep.tagMode ?? '')
                          }
                          options={spec.seg.options}
                          onChange={(v) => {
                            if (spec.seg?.key === 'vendor') {
                              updatePrep(id, {
                                vendor: v,
                                ...(spec.seg.resetsFile ? { file: null, files: [] } : {})
                              })
                            } else {
                              updatePrep(id, {
                                tagMode: v,
                                ...(isExternalHistorySource(id)
                                  ? { fields: { mappingValid: 'false' } }
                                  : {})
                              })
                            }
                          }}
                        />
                      ) : null}

                      {spec.schema ? (
                        <div className="rounded-md border border-line-2 bg-bg-sunk px-3 py-2.5">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-xs font-medium text-ink">
                                Expected CSV columns
                              </div>
                              <p className="mt-0.5 text-10 leading-snug text-ink-3">
                                Row values override wizard defaults. Header aliases are accepted.
                              </p>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={tagImportTemplateState.isFetching}
                              onClick={() =>
                                downloadTagImportTemplate(spec.schema!.templateProtocol)
                              }
                            >
                              <Download className="size-3.5" />
                              Download CSV template
                            </Button>
                          </div>
                          <SchemaColumns label="Required" columns={spec.schema.required} />
                          <SchemaColumns label="Optional" columns={spec.schema.optional} />
                          {templateError ? (
                            <p role="alert" className="mt-2 text-10 text-st-down">
                              {templateError}
                            </p>
                          ) : null}
                        </div>
                      ) : null}

                      {spec.drop ? (
                        <div className="flex flex-col gap-2">
                          <FileDropZone
                            accept={spec.drop.accept}
                            label={spec.drop.label}
                            helperText={spec.drop.helper}
                            initialFile={prep.file ?? prep.files[0] ?? null}
                            initialFiles={
                              id === 'unstructured' || id === 'tabular' || id === 'plc'
                                ? prep.files
                                : undefined
                            }
                            onFilesSelected={(files) => {
                              const list = Array.from(files)
                              if (list.length === 0) return
                              if (id === 'unstructured' || id === 'tabular' || id === 'plc') {
                                updatePrep(id, { file: list[0] ?? null, files: list })
                              } else {
                                updatePrep(id, { file: list[0] ?? null, files: [] })
                              }
                            }}
                            onFilesCleared={() => updatePrep(id, { file: null, files: [] })}
                          />
                          {fileMismatch ? (
                            <div
                              role="status"
                              className="flex gap-2 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
                            >
                              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                              <span>
                                {mismatchedFileName} does not match the accepted types for this
                                source ({spec.drop.accept || 'any'}). Switch the source system back,
                                or choose compatible files.
                              </span>
                            </div>
                          ) : null}
                          {id === 'dbdump' && prep.file && !fileMismatch ? (
                            <DumpFilePreview file={prep.file} />
                          ) : null}
                          {id === 'tabular' ? <TabularFileFields wizard={wizard} /> : null}
                        </div>
                      ) : null}

                      {spec.seg?.after ? (
                        <SegControl
                          label={spec.seg.label}
                          value={prep.tagMode ?? 'webdev'}
                          options={spec.seg.options}
                          onChange={(v) => updatePrep(id, { tagMode: v })}
                        />
                      ) : null}

                      {spec.fields.length > 0 ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {spec.fields.map((f) => (
                            <div
                              key={f.id}
                              className={cn(
                                'flex flex-col gap-1.5',
                                f.span === 2 && 'sm:col-span-2'
                              )}
                            >
                              <Label className="text-11 font-medium text-ink-2">{f.label}</Label>
                              {f.pem ? (
                                <PemFileInput
                                  id={f.id}
                                  label={f.label}
                                  kind={f.pem}
                                  required={f.required}
                                  onChange={(text) => updatePrep(id, { fields: { [f.id]: text } })}
                                />
                              ) : f.area ? (
                                <Textarea
                                  value={prep.fields[f.id] ?? ''}
                                  placeholder={f.placeholder}
                                  rows={3}
                                  onChange={(e) =>
                                    updatePrep(id, {
                                      fields: {
                                        [f.id]: e.target.value,
                                        ...(isExternalHistorySource(id)
                                          ? { mappingValid: 'false' }
                                          : {})
                                      }
                                    })
                                  }
                                />
                              ) : (
                                <Input
                                  type={f.type ?? 'text'}
                                  value={prep.fields[f.id] ?? ''}
                                  placeholder={f.placeholder}
                                  onChange={(e) =>
                                    updatePrep(id, {
                                      fields: {
                                        [f.id]: e.target.value,
                                        ...(isExternalHistorySource(id)
                                          ? { mappingValid: 'false' }
                                          : {})
                                      }
                                    })
                                  }
                                />
                              )}
                              {f.help ? (
                                <p className="text-10 leading-snug text-ink-3">{f.help}</p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {id === 'scada' && prep.tagMode === 'gateway_link' ? (
                        <GatewayLinkSetup
                          gatewayUrl={prep.fields.gatewayUrl ?? ''}
                          fields={prep.fields}
                          siteId={siteId}
                        />
                      ) : null}

                      {id === 'soap' ? (
                        <SoapContractPreview
                          prep={prep}
                          onSelectionChange={(operations, contractHash) =>
                            updatePrep(id, {
                              fields: {
                                enabledOperations: JSON.stringify(operations),
                                soapPreviewContractHash: contractHash ?? ''
                              }
                            })
                          }
                        />
                      ) : null}

                      {(spec.checks ?? []).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="flex gap-2.5 border-0 bg-transparent p-0 text-left"
                          onClick={() =>
                            updatePrep(id, {
                              checks: { [c.id]: !prep.checks[c.id] },
                              ...(isExternalHistorySource(id)
                                ? { fields: { mappingValid: 'false' } }
                                : {})
                            })
                          }
                        >
                          <Checkbox
                            checked={!!prep.checks[c.id]}
                            className="mt-0.5 pointer-events-none"
                            tabIndex={-1}
                          />
                          <span className="min-w-0">
                            <span className="block text-xs font-medium text-ink">{c.label}</span>
                            <span className="mt-0.5 block text-10 leading-snug text-ink-3">
                              {c.help}
                            </span>
                          </span>
                        </button>
                      ))}

                      {id === 'tagmap' ? (
                        <TagMappingStudio
                          grammarsJson={prep.fields.grammars ?? ''}
                          manualLinksJson={prep.fields.manualLinks ?? ''}
                          suggestJobId={prep.fields.suggestJobId}
                          mappingTypeId={prep.fields.mappingTypeId}
                          additionalContext={prep.fields.additionalContext}
                          siteId={siteId}
                          onGrammarsChange={(grammarsJson) =>
                            updatePrep(id, { fields: { grammars: grammarsJson } })
                          }
                          onManualLinksChange={(manualLinksJson) =>
                            updatePrep(id, { fields: { manualLinks: manualLinksJson } })
                          }
                          onSuggestJobIdChange={(jobId) =>
                            updatePrep(id, { fields: { suggestJobId: jobId } })
                          }
                          onMappingTypeIdChange={(mappingTypeId) =>
                            updatePrep(id, {
                              fields: {
                                mappingTypeId,
                                grammars: '',
                                manualLinks: '',
                                suggestJobId: ''
                              }
                            })
                          }
                          onAdditionalContextChange={(value) =>
                            updatePrep(id, { fields: { additionalContext: value } })
                          }
                        />
                      ) : null}
                      {isExternalHistorySource(id) &&
                      prep.tagMode === 'existing_tags' &&
                      defersExternalHistoryMapping(state, id) ? (
                        <div className="rounded-md border border-st-info/30 bg-st-info-bg px-3 py-2 text-xs text-ink-2">
                          {SOURCE_LABELS[id]} mapping will open during the run after the selected
                          SCADA / OPC tag sources finish ingesting.
                        </div>
                      ) : isExternalHistorySource(id) && prep.tagMode === 'existing_tags' ? (
                        <ExternalHistoryMappingStudio
                          provider={id}
                          siteId={siteId}
                          baseUrl={prep.fields.baseUrl ?? ''}
                          credentials={externalHistoryCredentials(id, prep, sub)}
                          skipTlsVerify={!!prep.checks.skipTlsVerify}
                          nameFilter={prep.fields.nameFilter}
                          rootPath={prep.fields.rootPath}
                          historySources={parseExternalHistorySources(prep.fields.historySources)}
                          grammarsJson={prep.fields.grammars ?? ''}
                          manualLinksJson={prep.fields.manualLinks ?? ''}
                          suggestJobId={prep.fields.suggestJobId}
                          additionalContext={prep.fields.additionalContext}
                          onHistorySourcesChange={(historySources) =>
                            updatePrep(id, {
                              fields: { historySources: JSON.stringify(historySources) }
                            })
                          }
                          onGrammarsChange={(grammars) => updatePrep(id, { fields: { grammars } })}
                          onManualLinksChange={(manualLinks) =>
                            updatePrep(id, { fields: { manualLinks } })
                          }
                          onSuggestJobIdChange={(suggestJobId) =>
                            updatePrep(id, { fields: { suggestJobId } })
                          }
                          onAdditionalContextChange={(additionalContext) =>
                            updatePrep(id, { fields: { additionalContext } })
                          }
                          onMappingValidChange={(valid) =>
                            updatePrep(id, {
                              fields: { mappingValid: valid ? 'true' : 'false' }
                            })
                          }
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <MappingSkipConfirmDialog
        open={confirmSkipOpen}
        onOpenChange={setConfirmSkipOpen}
        mappings={skippedMappings}
        onConfirm={() => {
          setConfirmSkipOpen(false)
          continueToNext()
        }}
      />
    </WizardChrome>
  )
}

function SchemaColumns({ label, columns }: { label: string; columns: readonly string[] }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-10 font-medium text-ink-3">{label}</span>
      {columns.map((column) => (
        <code
          key={column}
          className="rounded border border-line bg-bg-elev px-1.5 py-0.5 text-10 text-ink-2"
        >
          {column}
        </code>
      ))}
    </div>
  )
}

function SegControl({
  label,
  value,
  options,
  onChange
}: {
  label: string
  value: string
  options: Array<{ id: string; label: string; disabled?: boolean }>
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <span className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">{label}</span>
      <ToggleGroup
        type="single"
        size="sm"
        variant="outline"
        value={value}
        onValueChange={(v) => {
          if (v) onChange(v)
        }}
      >
        {options.map((o) => (
          <ToggleGroupItem
            key={o.id}
            value={o.id}
            disabled={o.disabled}
            className="h-7 px-2.5 text-xs"
          >
            {o.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}

function SourceSubSeg({
  id,
  value,
  onChange
}: {
  id: SourceId
  value?: SubOptionId
  onChange: (v: SubOptionId) => void
}) {
  const options =
    id === 'scada'
      ? [
          { id: 'ignition' as const, label: 'Ignition' },
          { id: 'aveva' as const, label: 'Aveva' }
        ]
      : id === 'safetymanager'
        ? [
            { id: 'legacy' as const, label: 'Legacy' },
            { id: '15.2' as const, label: '15.2' }
          ]
        : [
            { id: 'ignition' as const, label: 'Connected through Ignition' },
            { id: 'standalone' as const, label: 'Standalone DB' }
          ]
  const label = id === 'scada' ? 'Source system' : id === 'safetymanager' ? 'Version' : 'Connection'
  const fallback = id === 'safetymanager' ? 'legacy' : 'ignition'
  return (
    <SegControl
      label={label}
      value={value ?? fallback}
      options={options}
      onChange={(v) => onChange(v as SubOptionId)}
    />
  )
}
